/**
 * RecentSignIns — Iter-110 · Item 6.
 * Profile card listing the user's last 10 sign-ins (device, browser,
 * timestamp, city). Informational only — suspicious activity should be
 * reported to an admin.
 */
import { useEffect, useState } from 'react';
import { Monitor, Smartphone, Tablet, History } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DEVICE_ICON = {
  Mobile: Smartphone,
  Tablet: Tablet,
  Desktop: Monitor,
};

export const RecentSignIns = () => {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/auth/login-activity`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setEntries(d?.entries || []))
      .catch(() => setEntries([]));
  }, []);

  if (!entries) return null;

  return (
    <div
      className="rounded-xl p-5"
      style={{ backgroundColor: '#ffffff', border: '1px solid rgba(0,0,0,0.12)' }}
      data-testid="recent-signins-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <History className="h-5 w-5" style={{ color: '#C9A962' }} />
        <p className="font-bold text-sm uppercase tracking-wider" style={{ color: '#000' }}>
          Recent Sign-Ins
        </p>
      </div>
      <p className="text-xs mb-4" style={{ color: '#555' }}>
        Your last {Math.min(entries.length, 10) || 10} sign-ins. See something unfamiliar? Contact your NECS admin.
      </p>

      {entries.length === 0 ? (
        <p className="text-xs" style={{ color: '#888' }} data-testid="recent-signins-empty">
          No sign-in history yet — it starts recording from your next login.
        </p>
      ) : (
        <div className="space-y-1.5" data-testid="recent-signins-list">
          {entries.map((e, i) => {
            const Icon = DEVICE_ICON[e.device] || Monitor;
            let when = e.ts;
            try {
              when = new Date(e.ts).toLocaleString([], {
                month: 'short', day: 'numeric', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
              });
            } catch { /* keep raw */ }
            return (
              <div
                key={`${e.ts}-${i}`}
                className="flex items-center gap-3 px-3 py-2 rounded-lg"
                style={{ backgroundColor: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.08)' }}
                data-testid={`recent-signin-row-${i}`}
              >
                <Icon className="h-4 w-4 flex-shrink-0" style={{ color: '#C9A962' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate" style={{ color: '#000' }}>
                    {e.device} · {e.browser}
                    {e.method === '2fa' && (
                      <span className="ml-1.5 text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(16,185,129,0.12)', color: '#059669' }}>2FA</span>
                    )}
                  </p>
                  <p className="text-[11px]" style={{ color: '#666' }}>{e.city || 'Unknown'}</p>
                </div>
                <p className="text-[11px] whitespace-nowrap" style={{ color: '#555' }}>{when}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RecentSignIns;
